// ─── The attribute catalog: units and typed-attribute definitions ───
// MIRROR of pim/app/gnp/services/attributeCatalog.ts. Edit the pim copy and
// copy it across.
//
// At runtime the site reads units from Cosmos PIM.UnitOfMeasure and definitions
// from PIM.Attribute; this copy is the fallback and the type source.
//
// Conversion factors are physical constants, not configuration — 12 inches to
// the foot does not belong behind a knob.
//
// Plan: docs/plans/PLAN_PIM_ATTRIBUTE_TYPED_VALUES.md

import type { AttributeDef, UnitOfMeasure } from "./attributeValue";

// Ids carry over from the legacy pim1.UnitOfMeasure rows so the existing
// UnitOfMeasureId contract does not move. 6 is new (Millimetres) — the corpus
// holds "120 mm", "30mm", "38 mm".
export const UNITS: UnitOfMeasure[] = [
  { Id: 1, Name: "Inches", Symbol: '"', Dimension: "length", ToBase: 1, ToBaseOffset: null, DisplayStyle: null },
  { Id: 2, Name: "Feet", Symbol: "'", Dimension: "length", ToBase: 12, ToBaseOffset: null, DisplayStyle: "feetInches" },
  { Id: 3, Name: "Amp Hours", Symbol: "Ah", Dimension: "charge", ToBase: 1, ToBaseOffset: null, DisplayStyle: null },
  { Id: 4, Name: "Pounds", Symbol: "lbs", Dimension: "mass", ToBase: 1, ToBaseOffset: null, DisplayStyle: null },
  { Id: 5, Name: "Gallons", Symbol: "Gal", Dimension: "volume", ToBase: 1, ToBaseOffset: null, DisplayStyle: null },
  { Id: 6, Name: "Millimetres", Symbol: "mm", Dimension: "length", ToBase: 0.039370079, ToBaseOffset: null, DisplayStyle: null },
  { Id: 8771, Name: "Kilograms", Symbol: "kg", Dimension: "mass", ToBase: 2.204622622, ToBaseOffset: null, DisplayStyle: null },
];

const LENGTH = { Dimension: "length", BaseUnitOfMeasureId: 1, DisplayUnitOfMeasureId: 1, Decimals: 2, Suffix: '"' };
const MASS = { Dimension: "mass", BaseUnitOfMeasureId: 4, DisplayUnitOfMeasureId: 4, Decimals: 0, Suffix: "lbs" };
const COUNT = { Decimals: 0, Suffix: "" };

// "Hrs"/"KH"/"HD" on an attribute already named "... Hours" restate the name and
// carry no unit. Declared per attribute, never globally — "KH" is only
// meaningless on Key Hours.
const HOUR_LABELS = ["Hr", "Hrs", "Hour", "Hours"];

/**
 * The twelve attributes this migration converts (§5 of the plan). Everything
 * absent from this map stays a string — including Size, Capacity, Hours, Gauge,
 * Carriage Class and Attachment Opening, each of which the full-corpus profile
 * showed to be a modelling problem rather than a typing one.
 */
export const TYPED_ATTRIBUTES: Record<string, Partial<AttributeDef>> = {
  Length: LENGTH,
  Width: LENGTH,
  Height: LENGTH,
  Depth: LENGTH,
  "Height Lowered": LENGTH,
  "Height Raised": LENGTH,
  "Fork Length": LENGTH,
  Weight: { ...MASS, SupportsEstimated: true },
  "Weight w/ Battery": MASS,
  Year: COUNT,
  "Key Hours": { ...COUNT, LabelSuffixes: [...HOUR_LABELS, "KH"] },
  "Deadman Hours": { ...COUNT, LabelSuffixes: [...HOUR_LABELS, "HD"] },
};

export const isTypedAttribute = (name: string) => name in TYPED_ATTRIBUTES;

/**
 * Conversion factors are physical constants, so an empty list from a caller is
 * a plumbing failure, not a configuration choice — fall back to the authored
 * table rather than silently rendering values with no unit.
 */
export const unitsOrDefault = (units: UnitOfMeasure[] | null | undefined): UnitOfMeasure[] =>
  units?.length ? units : UNITS;

/** Returns null for an attribute this migration deliberately leaves as a string. */
export function getAttributeDef(name: string, attributeId = 0): AttributeDef | null {
  const overrides = TYPED_ATTRIBUTES[name];
  if (!overrides) return null;
  return { Id: attributeId, Name: name, ValueType: 1, Prefix: "", ...overrides };
}
